import { useContext, useEffect, useMemo, useState } from 'react';
import {
  StudentAssignmentItemWithAttempt,
  StudentAssignmentOrderByType,
  StudentProgress,
} from '../../../../constants/types/lms/student/homework/type';
import StudentAssignmentSidebar from './StudentAssignmentSidebar';
import StudentAssignmentTabs from './StudentAssignmentTabs';
import StudentAssignmentListPanel from './StudentAssignmentListPanel';
import StudentProgressMapPanel from './StudentProgressMapPanel';
import StudentRecitationProgressMapPanel from './StudentRecitationProgressMapPanel';
import { LoadingSpinner } from '../../../Loading/LoadingSpinner';
import { useAppDispatch } from '../../../../store/hook';
import {
  getAssignmentAttemptInfo,
  getStudentAssignmentList,
  getStudentProgress,
} from '../../../../api/utils/lms/student/homwork/api';
import { openAlert } from '../../../../store/features/modalSlice';
import { getErrorMessages } from '../../../../api/utils/util-func';
import { StudentClassDetailContext } from '../../../../pages/student/class/StudentClassDetailContext';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Button, Input, Select } from 'antd';
import { useAssignmentCategory } from '../../../../hooks/lms/student/homework/useAssignmentCategory';
import {
  ASSIGNMENT_STATUS_LABEL_MAP,
  AssignmentStatusType,
} from '../../../../constants/types/lms/type';

export type AssignmentTab = 'ASSIGNMENT_LIST' | 'PROGRESS_MAP';

const VISIBLE_ASSIGNMENT_STATUS_LIST = [
  AssignmentStatusType.IN_PROGRESS,
  AssignmentStatusType.COMPLETED,
];

const ASSIGNMENT_STATUS_OPTIONS = [
  ...VISIBLE_ASSIGNMENT_STATUS_LIST.map((value) => ({
    label: ASSIGNMENT_STATUS_LABEL_MAP[value],
    value,
  })),
];

const STUDENT_ASSIGNMENT_ORDER_OPTIONS = [
  {
    label: '최신순',
    value: StudentAssignmentOrderByType.LATEST,
  },
  {
    label: '오래된순',
    value: StudentAssignmentOrderByType.OLDEST,
  },
  {
    label: '남은 날짜순',
    value: StudentAssignmentOrderByType.REMAIN_DAYS,
  },
];

/**
 * 개편 LMS (학생) - 클래스 상세 페이지 > 콘텐츠 판넬
 */
const StudentAssignmentPanel = () => {
  const navigate = useNavigate();

  const { classId } = useParams();

  const dispatch = useAppDispatch();

  const { classDetail } = useContext(StudentClassDetailContext);

  const { classType, selectedCategory, onCategoryChange } =
    useAssignmentCategory(classDetail);

  const [searchParams, setSearchParams] = useSearchParams();

  const { status, orderBy } = useMemo(() => {
    return {
      status: searchParams.get('status') || undefined,
      orderBy: searchParams.get('orderBy') || undefined,
    };
  }, [searchParams]);

  const [searchOption, setSearchOption] = useState('book');
  const [searchValue, setSearchValue] = useState('');

  const [selectedTab, setSelectedTab] =
    useState<AssignmentTab>('ASSIGNMENT_LIST');

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [assignmentList, setAssignmentList] = useState<
    StudentAssignmentItemWithAttempt[]
  >([]);
  const [progress, setProgress] = useState<StudentProgress | null>(null);

  /** 클래스별 [할 일 목록, 진도 지도] 데이터 조회 함수 */
  const fetchAssignmentInfo = async () => {
    try {
      setIsLoading(true);

      const responseAssignmentList = await getStudentAssignmentList({
        classId,
        status: AssignmentStatusType[status as AssignmentStatusType],
        ...(searchValue
          ? {
              [searchOption]: searchValue,
            }
          : {}),
        orderBy:
          StudentAssignmentOrderByType[orderBy as StudentAssignmentOrderByType],
      });

      const responseProgress = await getStudentProgress(classId);

      const availableAssignmentList = (
        responseAssignmentList?.items ?? []
      ).filter((assignment) => {
        const dueDateEndOfDay = new Date(assignment.dueDate);
        dueDateEndOfDay.setHours(23, 59, 59, 999);

        /** 마감된 과제는 노출 X */
        return dueDateEndOfDay >= new Date();
      });

      const assignmentListWithAttempt = await Promise.all(
        availableAssignmentList.map(async (assignment) => {
          try {
            const attemptInfo = await getAssignmentAttemptInfo(
              assignment.todoId,
            );

            return {
              ...assignment,
              attemptCount: attemptInfo.attemptCount,
              attemptLimit: attemptInfo.attemptLimit,
            };
          } catch {
            return {
              ...assignment,
              attemptCount: 0,
              attemptLimit: 0,
            };
          }
        }),
      );

      const filtered = assignmentListWithAttempt.filter((assignment) => {
        /** 응시 횟수와 응시 제한 횟수가 같은 과제는 노출 X */
        return assignment.attemptCount !== assignment.attemptLimit;
      });

      setAssignmentList(filtered ?? []);
      setProgress(responseProgress ?? null);
    } catch (error) {
      dispatch(openAlert({ message: getErrorMessages(error) }));
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchAssignmentInfo();
  }, [status, orderBy]);

  const handleSearch = async () => {
    await fetchAssignmentInfo();
  };

  const handleBack = () => {
    navigate(`/student/class`);
  };

  return (
    <section className="mx-auto w-full rounded-xl bg-white p-4 shadow-card xl:w-[976px] xl:max-w-[976px] xl:p-6">
      <div className="mx-auto w-full max-w-[1180px]">
        <Button
          type="default"
          className="mb-4 flex items-center gap-2 rounded-lg px-5"
          onClick={handleBack}
        >
          ← 내 학습 목록으로
        </Button>

        <div className="grid grid-cols-1 gap-4 xl:grid-cols-[260px_1fr]">
          <StudentAssignmentSidebar
            classType={classType}
            assignmentList={assignmentList}
            progress={progress}
            selectedCategory={selectedCategory}
            onCategoryChange={onCategoryChange}
          />

          <div className="min-w-0">
            <StudentAssignmentTabs
              selectedTab={selectedTab}
              onTabChange={setSelectedTab}
            />

            {isLoading ? (
              <div className="mt-5">
                <LoadingSpinner message="데이터 조회중" />
              </div>
            ) : (
              <div className="mt-5">
                {selectedTab === 'ASSIGNMENT_LIST' ? (
                  <>
                    <div className="mb-5 flex flex-col gap-3 xl:flex-row xl:flex-wrap xl:items-center">
                      <div className="flex w-full gap-2 xl:w-auto">
                        <Select
                          className="w-[100px] shrink-0"
                          options={[{ value: 'book', label: '북' }]}
                          value={searchOption}
                          onChange={setSearchOption}
                        />

                        <Input
                          type="search"
                          className="min-w-0 flex-1 xl:w-[200px] xl:flex-none"
                          placeholder="검색어를 입력하세요"
                          value={searchValue}
                          onChange={(event) =>
                            setSearchValue(event.target.value)
                          }
                          onPressEnter={handleSearch}
                          allowClear
                        />

                        <Button
                          className="shrink-0 rounded-lg !border-purple5 !bg-purple5 !text-white hover:!border-purple6 hover:!bg-purple6 hover:!text-white active:!border-purple7 active:!bg-purple7"
                          onClick={handleSearch}
                        >
                          검색
                        </Button>
                      </div>

                      <Select
                        className="w-full xl:w-[120px] xl:shrink-0"
                        placeholder="과제 상태"
                        options={ASSIGNMENT_STATUS_OPTIONS}
                        value={status}
                        allowClear
                        onChange={(value) => {
                          const nextParams = new URLSearchParams(searchParams);

                          if (value) {
                            nextParams.set('status', value);
                          } else {
                            nextParams.delete('status');
                          }

                          setSearchParams(nextParams);
                        }}
                      />

                      <Select
                        className="w-full xl:w-[120px]"
                        placeholder="정렬"
                        options={STUDENT_ASSIGNMENT_ORDER_OPTIONS}
                        value={orderBy}
                        allowClear
                        onChange={(value) => {
                          const nextParams = new URLSearchParams(searchParams);

                          if (value) {
                            nextParams.set('orderBy', value);
                          } else {
                            nextParams.delete('orderBy');
                          }

                          setSearchParams(nextParams);
                        }}
                      />
                    </div>

                    <StudentAssignmentListPanel
                      selectedCategory={selectedCategory}
                      assignmentList={assignmentList}
                      fetchAssignmentInfo={fetchAssignmentInfo}
                    />
                  </>
                ) : selectedCategory !== 'RECITATION' ? (
                  <StudentProgressMapPanel
                    selectedCategory={selectedCategory}
                    progress={progress}
                  />
                ) : (
                  <StudentRecitationProgressMapPanel progress={progress} />
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default StudentAssignmentPanel;
